import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import flatMap from 'lodash/flatMap';
import { createGlobalState } from '.';
import { filterActiveClasses } from '../../utils/archiveFilter';

// staleTime tiers (ms)
const USER_DATA = 5 * 60_000; // 5 min — profile, classes, preferences
//
// Round 7 follow-up: widen DYNAMIC_DATA from 60s to 3min for read-heavy
// list endpoints (tasks, assignments, completed-tasks, documents,
// community-tasks, give-feedback-completed-tasks). These were fetched
// repeatedly on tab focus / route-back even when the user had just left
// the page seconds before. 3min is short enough to still feel fresh
// when the user creates/edits a task and navigates back, but cuts the
// repeat-fetch noise dramatically.
//
// NOT widened: notifications, comments (commentsById,
// overAllCommentsById). Those need to feel real-time — leave at 60s.
const DYNAMIC_DATA = 3 * 60_000; // 3 min — tasks, submissions, feedbacks

import {
  getAllMarkingCriteria,
  getAssignments,
  getClasses,
  getClassesWithMembers,
  getCommentBank,
  getCommentsForSubmission,
  getCompletedTasks,
  getCommunityTasks,
  getFeedbackBanks,
  getGiveFeedbackCompletedTasks,
  getOtherDrafts,
  getOverComments,
  getSubmissionById,
  getSubmissionsByAssignmentId,
  // Round 7 follow-up cleanup: getTeachersForClass import removed (no callers
  // in this file). The function is still exported from service.js because
  // singular /classes/{id}/teachers is a valid API; consumers that need
  // teachers-only without the full bundle import it directly from service.
  getModelResponses,
  getClassSettingForClass,
  getIsJeddAIEnabled,
  getDocuments,
  getNotifications,
  getProfile,
  getTasks,
  getCommentBanksforStudents,
  getMarkingCriteriasforStudents,
  getShortAnswersForStudents,
  getSuccessCriteriaForStudents,
  getPreferenceMarkingCriteria,
  getAssignmentById,
  fetchSharedSubmission,
  getAssignmentStatusById,
  getAllMarkingCriteriaWithPreferences,
  getAllCommentBanksWithPreferences,
  getCommentBankPreferences,
  getShares,
  getAllSuccessCriteria,
  getAllSuccessCriteriaWithPreferences,
  getAllShortAnswers,
  getAllShortAnswersWithPreferences,
  getSuccessCriteriaPreferences,
  getShortAnswerPreferences,
  getAdvancedMarkingTeacherSettings,
  getAdvancedMarkingClassOverrides,
  getAdvancedMarkingStudentSettings,
  getAdvancedMarkingEffectiveSettings,
  getAdvancedMarkingStudentClassEffectiveSettings,
  fetchSuccessCriteriaStats,
  getAssignmentsByClassId,
  getStudentsForClass,
  fetchSubmissionsStats,
  fetchCommentBanksStats,
  fetchCatalog,
  getTeachersCommentBanks,
  fetchRubricsStats,
  fetchRubricsPercentageStats,
  getTeachersRubrics,
  fetchShortAnswerStats,
  getTeachersShortAnswers,
} from '../../service';
import { createGlobalStates } from './createGlobalStates';

export const useAllSubmisssionsById = createGlobalState(
  'submissionsByAssignmentId',
  getSubmissionsByAssignmentId
);

export const useCommentBanksById = createGlobalStates(
  'feedbackBanksById',
  getCommentBank,
  USER_DATA
);
export const useClassSettingById = createGlobalStates(
  'classSettingById',
  getClassSettingForClass,
  USER_DATA
);
export const useAssignmentsAll = createGlobalState(
  'assignments',
  getAssignments,
  DYNAMIC_DATA
);
export const useCompletedAll = createGlobalState(
  'completedTasks',
  getCompletedTasks,
  DYNAMIC_DATA
);
export const useAllDocuments = createGlobalState(
  'allDocuments',
  getDocuments,
  DYNAMIC_DATA
);
// Notifications stay at the 60s default — they need to feel real-time.
export const useNotifications = createGlobalState(
  'notifications',
  getNotifications
);
export const useCommentBanks = createGlobalState(
  'feedbackBanks',
  getFeedbackBanks,
  USER_DATA
);
export const useCommentBanksWithPreferences = createGlobalState(
  'commentBanksWithPreferences',
  getAllCommentBanksWithPreferences,
  USER_DATA
);
export const useCommentBankPreferences = createGlobalState(
  'commentBankPreferences',
  getCommentBankPreferences,
  USER_DATA
);
export const useMarkingCriterias = createGlobalState(
  'markingCriterias',
  getAllMarkingCriteria,
  USER_DATA
);
export const useMarkingCriteriasWithPreferences = createGlobalState(
  'markingCriteriasWithPreferences',
  getAllMarkingCriteriaWithPreferences,
  USER_DATA
);
export const useTasks = createGlobalState('tasks', getTasks, DYNAMIC_DATA);
export const useCommunityTasks = createGlobalState(
  'communityTasks',
  getCommunityTasks,
  DYNAMIC_DATA
);

export const useGiveFeedbackCompletedTasks = createGlobalState(
  'giveFeedbackCompletedTasks',
  getGiveFeedbackCompletedTasks,
  DYNAMIC_DATA
);

// Comments stay at 60s — visible-thread refresh expectation, low-volume endpoint.
export const useCommentsById = createGlobalState(
  'commentsById',
  getCommentsForSubmission,
  60_000,
  { keepPreviousData: false }
);

export const useOverAllCommentsById = createGlobalState(
  'overAllCommentsById',
  getOverComments,
  60_000,
  { keepPreviousData: false }
);
// Mobile's feedback view reads the live (version: null) comments and summary
// through the entries above, which never refetch on mount. A resubmission is
// marked while the view is closed or showing an earlier attempt, and
// MobileEditor only refreshes the submission and its revision-scoped comments
// — so reopening, or Back to latest, served the previous attempt's summary
// beside the new mark until a reload. Each time the view enters the live
// attempt, refetch exactly those two entries after commit and report `false`
// until a GET started after that point has settled, so the caller holds the
// live paint. A read already in flight may have been answered before the new
// mark, so it is left to finish (never cancelled under another observer) and
// then fetched again (as prefetchSubmissionFeedback does). A failed refetch
// leaves the query in error, which the caller shows instead of the cached
// summary. Versioned entries, revision-scoped entries and other submissions
// are untouched.
export function useFreshLiveSubmissionFeedback(submissionId, enabled = true) {
  const queryClient = useQueryClient();
  const key = submissionId == null ? '' : String(submissionId);
  const [freshFor, setFreshFor] = useState(null);
  useEffect(() => {
    if (!enabled || !key) return undefined;
    let alive = true;
    const inFlight = (queryKey) =>
      (queryClient.getQueryState(queryKey)?.fetchStatus ?? 'idle') !== 'idle';
    const refresh = async (queryKey) => {
      const refetch = () =>
        queryClient.refetchQueries(
          { queryKey, exact: true },
          { cancelRefetch: false }
        );
      if (inFlight(queryKey)) await refetch();
      await refetch();
      // Anything still running started after this view opened.
      while (alive && inFlight(queryKey)) await refetch();
    };
    Promise.all(
      ['commentsById', 'overAllCommentsById'].map((name) =>
        refresh([name, { submissionId, version: null }])
      )
    ).finally(() => {
      if (alive) setFreshFor(key);
    });
    return () => {
      alive = false;
      setFreshFor(null);
    };
  }, [enabled, key, submissionId, queryClient]);
  return !key || (enabled && freshFor === key);
}
export const useModelResponces = createGlobalState(
  'modelResponces',
  getModelResponses
);

export const useSubmissionById = createGlobalState(
  'submissionById',
  getSubmissionById,
  60_000,
  // Never show the previous submission when the route id changes (bulk upload,
  // student dropdown, etc.) — keepPreviousData caused URL≠data mismatches.
  { keepPreviousData: false }
);
export const useClasses = createGlobalState('classes', getClasses, USER_DATA);
export const useIsJeddAIEnabled = createGlobalState(
  'IsJeddAIEnabled',
  getIsJeddAIEnabled,
  USER_DATA
);

/**
 * Round 4 perf fix (frontend hot-spot α):
 * Previously: getClassesWithStudents() then Promise.all(getTeachersForClass(id)
 * per class) → 1 + C HTTP calls per login (C = teacher's class count, up to
 * 26 in production). Fires from App.jsx + TeacherTasksRoot + FeedbacksRoot
 * (and Navbar/Header2/Tasks/CreateNewTask via useActiveClassData).
 *
 * Now: ONE call to `/classes/with-members` (backend batches into ONE call
 * to the WP `/jeddle/v1/classes` endpoint). 1 HTTP roundtrip total.
 *
 * Return shape kept fully backward-compatible — flat array of class objects
 * with `students` and `teachers` populated. Existing consumers don't change.
 *
 * Member records are name-only (id, name) — verified no current consumer
 * reads .email/.avatar/.role on the class.students/class.teachers arrays.
 * If a future page needs those, fetch via /classes/{id}/students.
 *
 * Round 7 follow-up: AdvancedMarkingControls + CreateNewTask both migrated
 * to consume this shared hook (via useActiveClassData), so the legacy
 * getActiveClassesWithStudents + per-class getTeachersForClass fan-out is
 * now dead in feedbackUI. The local fetchClassWithStudentsAndTeachers /
 * useClassData copy that lived inside AdvancedMarkingControls has been
 * removed. Backend /classes/all/details is @Deprecated.
 */
async function fetchClassWithStudentsAndTeachers() {
  try {
    const resp = await getClassesWithMembers();
    const classes = resp.classes || [];
    // Round 5 codex finding #3: surface the degraded signal so membership-
    // sensitive consumers can act on it (banner, disable auto-accept, etc.)
    // Attached as a non-iterating property so existing .map/.filter/.length
    // consumers stay untouched (backward-compat). Read via classData?.degraded
    // — see useClassDataDegraded() helper hook below for the canonical
    // pattern.
    if (resp.degraded) {
      console.warn(
        'classData: backend returned degraded bundle (partial WP response)'
      );
      Object.defineProperty(classes, 'degraded', {
        value: true,
        enumerable: false,
        configurable: true,
      });
    }
    return classes;
  } catch (error) {
    console.error('Error fetching classes with members:', error);
    throw error;
  }
}

export const useClassData = createGlobalState(
  'classData',
  fetchClassWithStudentsAndTeachers,
  USER_DATA
);

/**
 * Read-only helper for membership-sensitive consumers
 * (auto-accept toggles, Mark Now, share-with-class, reviewer selectors).
 * Returns true when the backend indicated the class bundle is from a
 * partial/failed WP response — UI should warn rather than acting on
 * possibly-incomplete teacher/student lists.
 *
 * Returns false on initial load (no data yet) — degraded only surfaces
 * after a fetch has completed.
 */
export const useClassDataDegraded = () => {
  const { data: classData } = useClassData();
  return Boolean(classData && classData.degraded);
};

/**
 * Returns only active classes for teachers. Use in places where archived
 * class data should not appear (My Drafts dropdown, Create Task class
 * selection). Falls back to all classes if teacher has no active groups.
 *
 * Forwards all arguments (id, condition, time) to the underlying
 * useClassData hook so callers like App.jsx can gate on isAuthenticated.
 */
/**
 * FE-BU21: `degraded` is attached non-enumerably so .map/.filter consumers stay
 * untouched — which is exactly why filterActiveClasses' new array dropped it.
 * Most consumers read the roster through here, so losing it here lost it
 * everywhere: a partial class list looked indistinguishable from a complete one.
 */
const carryDegraded = (filtered, source) => {
  if (!source?.degraded || !Array.isArray(filtered)) return filtered;
  Object.defineProperty(filtered, 'degraded', {
    value: true,
    enumerable: false,
    configurable: true,
  });
  return filtered;
};

export const useActiveClassData = (id, condition, time) => {
  const { data: classData, ...rest } = useClassData(id, condition, time);
  const filtered = carryDegraded(filterActiveClasses(classData), classData);
  return { data: filtered, ...rest };
};

export const useProfile = createGlobalState('profile', getProfile, USER_DATA);

export const useMarkingCriteriasForStudents = createGlobalState(
  'markingCriteriasforStudents',
  getMarkingCriteriasforStudents,
  USER_DATA
);

export const usePreferenceMarkingCriteria = createGlobalState(
  'preferenceMarkingCriteria',
  getPreferenceMarkingCriteria,
  USER_DATA
);

export const useCommentBanksForStudents = createGlobalState(
  'commentBanksforStudents',
  getCommentBanksforStudents,
  USER_DATA
);

export const useSharedSubmission = createGlobalState(
  'getsharedSubmission',
  fetchSharedSubmission
);

export const useAssignmentById = createGlobalState(
  'assignmentById',
  getAssignmentById
);

export const useAssignmentStatusById = createGlobalState(
  'assignmentStatusById',
  getAssignmentStatusById
);

export const useShares = createGlobalState('shares', getShares, USER_DATA);

export const useSuccessCriteria = createGlobalState(
  'successCriteria',
  getAllSuccessCriteria,
  USER_DATA
);

export const useSuccessCriteriaWithPreferences = createGlobalState(
  'successCriteriaWithPreferences',
  getAllSuccessCriteriaWithPreferences,
  USER_DATA
);

export const useShortAnswers = createGlobalState(
  'shortAnswers',
  getAllShortAnswers,
  USER_DATA
);

export const useShortAnswersWithPreferences = createGlobalState(
  'shortAnswersWithPreferences',
  getAllShortAnswersWithPreferences,
  USER_DATA
);

export const useShortAnswersForStudents = createGlobalState(
  'shortAnswersForStudents',
  getShortAnswersForStudents,
  USER_DATA
);

export const useSuccessCriteriaForStudents = createGlobalState(
  'successCriteriaForStudents',
  getSuccessCriteriaForStudents,
  USER_DATA
);

export const useSuccessCriteriaPreferences = createGlobalState(
  'successCriteriaPreferences',
  getSuccessCriteriaPreferences,
  USER_DATA
);

export const useShortAnswerPreferences = createGlobalState(
  'shortAnswerPreferences',
  getShortAnswerPreferences,
  USER_DATA
);

export const useAdvancedMarkingTeacherSettings = createGlobalState(
  'advancedMarkingTeacherSettings',
  getAdvancedMarkingTeacherSettings,
  USER_DATA
);

export const useAdvancedMarkingClassOverrides = createGlobalState(
  'advancedMarkingClassOverrides',
  getAdvancedMarkingClassOverrides,
  USER_DATA
);

export const useAdvancedMarkingStudentSettings = createGlobalState(
  'advancedMarkingStudentSettings',
  getAdvancedMarkingStudentSettings,
  USER_DATA
);

export const useAdvancedMarkingEffectiveSettings = createGlobalState(
  'advancedMarkingEffectiveSettings',
  getAdvancedMarkingEffectiveSettings,
  USER_DATA
);

export const useAdvancedMarkingStudentClassEffectiveSettings =
  createGlobalState(
    'advancedMarkingStudentClassEffectiveSettings',
    getAdvancedMarkingStudentClassEffectiveSettings,
    USER_DATA
  );

// Round 7 follow-up cleanup: useClassesWithStudents removed (zero callers).
// It wrapped getClassesWithStudents (legacy 1+C-per-class fan-out backed
// by /classes/all/details). All consumers migrated to useActiveClassData
// which reads from the batched /classes/with-members endpoint. The
// backend endpoint is now @Deprecated.

export const useSuccessCriteriaStats = createGlobalState(
  'successCriteriaStats',
  fetchSuccessCriteriaStats
);

export const useAssignmentsByClassId = createGlobalStates(
  'assignmentsByClassId',
  getAssignmentsByClassId
);

export const useStudentsByClassId = createGlobalStates(
  'studentsByClassId',
  getStudentsForClass
);

export const useCatalog = createGlobalState('statsCatalog', fetchCatalog);

export const useSubmissionsStats = createGlobalState(
  'submissionsStats',
  fetchSubmissionsStats
);

export const useTeacherCommentBanks = createGlobalState(
  'teacherCommentBanks',
  getTeachersCommentBanks
);

export const useCommentBanksStats = createGlobalState(
  'commentBanksStats',
  fetchCommentBanksStats
);

export const useTeacherRubrics = createGlobalState(
  'teacherRubrics',
  getTeachersRubrics
);

export const useRubricsStats = createGlobalState(
  'rubricsStats',
  fetchRubricsStats
);

export const useRubricsPercentageStats = createGlobalState(
  'rubricsPercentageStats',
  fetchRubricsPercentageStats
);

export const useTeacherShortAnswers = createGlobalState(
  'teacherShortAnswers',
  getTeachersShortAnswers
);

export const useShortAnswerStats = createGlobalState(
  'shortAnswerStats',
  fetchShortAnswerStats
);
